import { DisconnectOutlined, EditOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { Button, Space, Tag } from "antd";
import dayjs from "dayjs";
import React from "react";
import { $t, i18n } from "~/i18n.js";

import { ActualFlightTime } from "~/components/clock/actualFlightTime.jsx";
import { LandingIcon, WeGlideIcon } from "~/components/icon/icon.jsx";
import { FlightTagColumn } from "~/components/flight/sections/FlightTags.jsx";
import {
  OGNlandingsTijd,
  OGNstartTijd,
} from "~/components/flight/sections/Ogn.jsx";
import { parseMinutes } from "~/lib/localize.js";
import { compareClubKisten } from "~/redux/materiaal/materiaal.normalizers.js";

const Volg = ({ isDebug }) => ({
  title: "#",
  dataIndex: "volg_nummer",
  width: 35,
  sorter: (a, b, sortOrder) => {
    if (sortOrder === "ascend") {
      return a.volg_nummer - b.volg_nummer;
    }
    return b.volg_nummer - a.volg_nummer;
  },
  render: (text, flight) => {
    if (isDebug) {
      return (
        <span>
          {flight.volg_nummer} <br />
          <span className="gray">{flight.id}</span>
        </span>
      );
    }
    return text;
  },
});

const Status = () => ({
  title: $t("Status"),
  width: 100,
  filters: [
    { text: $t("Wachtrij"), value: "waiting" },
    { text: $t("Vliegend"), value: "flying" },
    { text: $t("Afgerond"), value: "completed" },
  ],
  onFilter: (value, flight) => {
    if (value === "completed")
      return flight.landings_tijd && !flight.is_deleted;
    if (value === "flying")
      return flight.start_tijd && !flight.landings_tijd && !flight.is_deleted;
    if (value === "waiting") return !flight.start_tijd && !flight.is_deleted;
    return false;
  },
  render: (_, flight) => {
    if (flight.is_deleted) {
      return <Tag color="red">{$t("Verwijderd")}</Tag>;
    }
    if (flight.landings_tijd) {
      return <Tag>{$t("Afgerond")}</Tag>;
    }
    if (flight.start_tijd) {
      return <Tag color="blue">{$t("Vliegend")}</Tag>;
    }
    return <Tag color="orange">{$t("Wachtrij")}</Tag>;
  },
});

const Kist = () => ({
  title: $t("Kist"),
  dataIndex: 'registratie',
    sorter: (a, b, sortOrder) => {
    if (sortOrder === "ascend") {
      return compareClubKisten(a, b);
    }
    return compareClubKisten(b, a);
  },
  render: (_, flight) => (
    <p>
      {flight.callsign} {flight.registratie} <br />
      {flight.type}
    </p>
  ),
});

const Gezagvoerder = () => ({
  title: $t("PIC/Instr"),
  dataIndex: "gezagvoerder_naam",
  sorter: (a, b, sortOrder) => {
    if (sortOrder === "ascend") {
      if (sortOrder === "ascend") {
        return ("" + a.gezagvoerder_naam).localeCompare(b.gezagvoerder_naam);
      }
    }
    return ("" + a.gezagvoerder_naam).localeCompare(b.gezagvoerder_naam);
  },
  render: (text, flight, index) => {
    if (flight.start_tijd && !flight.gezagvoerder_naam) {
      return (
        <Tag color="red" className="bounce">
          {$t("ontbreekt")}
        </Tag>
      );
    }
    if (flight.gezagvoerder_naam === "-") return <EyeInvisibleOutlined />;
    return <p>
      {flight.gezagvoerder_naam && !flight.gezagvoerder_id && <DisconnectOutlined  style={{marginRight: 4}}/>}
      {flight.gezagvoerder_naam}</p>;
  },
});

const Pax = () => ({
  title: $t("Pax/DBO/2e"),
  dataIndex: "tweede_inzittende_naam",
  sorter: (a, b, sortOrder) => {
    if (sortOrder === "ascend") {
      if (sortOrder === "ascend") {
          return ("" + a.tweede_inzittende_naam).localeCompare(b.tweede_inzittende_naam);
      }
    }
      return ("" + a.tweede_inzittende_naam).localeCompare(b.tweede_inzittende_naam);
  },
  render: (text, flight) => {
    if (flight.tweede_inzittende_naam === "-") return <EyeInvisibleOutlined />;
    return <p>
      {flight.tweede_inzittende_naam && !flight.tweede_inzittende_id && <DisconnectOutlined  style={{marginRight: 4}}/>}
      {flight.tweede_inzittende_naam}</p>;
  },
});

const Meth = () => ({
  title: $t("Meth."),
  dataIndex: "start_methode",
  width: 60,
  render: (t) => $t(t),
  sorter: (a, b, sortOrder) => {
    if (sortOrder === "ascend") {
      if (sortOrder === "ascend") {
        return ("" + a.start_methode).localeCompare(b.start_methode);
      }
    }
    return ("" + a.start_methode).localeCompare(b.start_methode);
  }
});

const Tijden = () => ({
  title: $t("Tijden"),
  width: 125,
  dataIndex: 'start_tijd',
  sorter: (a, b, sortOrder) => {
    if (sortOrder === "ascend") {
      return ("" + a.start_tijd).localeCompare(b.start_tijd);
    }
    return ("" + b.start_tijd).localeCompare(a.start_tijd);
  },
  render: (_, flight) => {
    return (
      <div className="row">
        {flight.start_tijd && (
          <OGNstartTijd
            ogn_indicator={flight.start_ogn}
            time={flight.start_tijd}
          />
        )}
        {flight.landings_tijd && (
          <OGNlandingsTijd
            ogn_indicator={flight.landings_ogn}
            time={flight.landings_tijd}
          />
        )}
        {flight.start_tijd && !flight.landings_tijd && flight.flarm && (
          <a
            target="_blank"
            rel="noopener noreferrer"
            title="WeGlide"
            href={"https://weglide.org/live/" + flight.flarm}
          >
            <WeGlideIcon />{" "}
          </a>
        )}
      </div>
    );
  },
});

const Duur = () => ({
  title: $t("Duur"),
  dataIndex: "vluchtduur",
  width: 70,
  sorter: (a, b) => a._vluchtduur - b._vluchtduur,
  render: (text, flight) => {
    if (flight.landings_tijd) return parseMinutes(text);
    if (flight.start_tijd) return <ActualFlightTime flight={flight} />;
  },
});

const Height = () => ({
  title: $t("Hoogte"),
  dataIndex: "height",
  width: 70,
  sorter: (a, b) => a._height - b._height,
  render: (value) => i18n.parseLocaleHeightString(value),
});

export const MobileFlightColumns = (isDebug) => [
  Volg({ isDebug }),
  Kist(),
  Gezagvoerder(),
  Pax(),
  Meth(),
  Tijden(),
  Duur(),
  Height(),
  FlightTagColumn(),
];

export const WideFlightColumns = (updateFlight, showLandingButton, isDebug) => {
  return [
    Volg({ isDebug }),
    showLandingButton ? Status() : null,
    Kist(),
    Gezagvoerder(),
    Pax(),
    Meth(),
    Tijden(),
    Duur(),
    Height(),
    FlightTagColumn(),
    {
      title: "",
      width: 80,
      render: (text, flight) => {
        if (flight.is_locked) return null;
        if (showLandingButton) {
          return (
            <Space>
              {!flight.landings_tijd && (
                <Button
                  disabled={
                    flight.is_deleted ||
                    !flight.registratie ||
                    !flight.start_tijd
                  }
                  onClick={() => {
                    updateFlight({
                      action: "land",
                      volg_nummer: flight.volg_nummer,
                      uuid: flight.uuid,
                      start_tijd: flight.start_tijd,
                      landings_tijd: dayjs().format("HH:mm"),

                      // add timestamp, because if we are going to land a flight manually, we only want to reset the redis state if it's a recent landing, to prevent deleting the state of a flight that is landed long ago but just updated now
                      timestamp: new Date().getTime(),
                    });
                  }}
                  type="primary"
                  icon={<LandingIcon />}
                  size="small"
                >
                  {$t("Land")}
                </Button>
              )}
            </Space>
          );
        }
        // in other cases show that you can edit
        return (
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            shape="circle"
          />
        );
      },
    },
  ].filter((f) => f);
};
